import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  Banner,
  BannerList,
} from "~/components/design-system/Banner/Banner.js";

export interface RecipientOutsideDataController {
  /** The name of the recipient (team or user) */
  recipientName: string | null | undefined;
  /** The name of the recipient's data controller */
  recipientDataControllerName: string | null | undefined;
}

export interface DataControllerWarningBannersProps {
  /** The name of the current user's data controller */
  userDataControllerName: string | null | undefined;
  /** List of recipients who are outside the user's data controller */
  recipientsOutsideDataController: RecipientOutsideDataController[];
  /** List of recipient names who are working independently */
  independentRecipientNames: string[];
}

export function DataControllerWarningBanners({
  userDataControllerName,
  recipientsOutsideDataController,
  independentRecipientNames,
}: DataControllerWarningBannersProps) {
  const { t } = useTranslation();

  // Deduplicate recipients by recipientName + recipientDataControllerName to avoid showing the same recipient twice
  // We include both fields because different organisations may have recipients with the same name (e.g., "Admin Team")
  const uniqueRecipientsOutsideDataController =
    recipientsOutsideDataController.filter(
      (recipient, index, self) =>
        index ===
        self.findIndex(
          (r) =>
            r.recipientName === recipient.recipientName &&
            r.recipientDataControllerName ===
              recipient.recipientDataControllerName,
        ),
    );

  // Deduplicate independent recipient names
  const uniqueIndependentRecipientNames = [
    ...new Set(independentRecipientNames),
  ];

  const hasOutsideDataControllerWarning =
    uniqueRecipientsOutsideDataController.length > 0;
  const hasIndependentWarning = uniqueIndependentRecipientNames.length > 0;

  return (
    <>
      {hasOutsideDataControllerWarning && (
        <Box sx={{ mb: 4 }}>
          <Banner
            title={t("dataController.sharingOutsideDataController.title")}
            body={[
              [
                t(
                  "dataController.sharingOutsideDataController.yourOrganisation",
                  {
                    organisationName:
                      userDataControllerName ?? t("dataController.unknown"),
                  },
                ),
              ],
              // Empty line for visual separation
              [""],
              ...uniqueRecipientsOutsideDataController.map((recipient) => [
                t(
                  "dataController.sharingOutsideDataController.recipientOrganisation",
                  {
                    recipientName:
                      recipient.recipientName ??
                      t("dataController.unknownRecipient"),
                    organisationName:
                      recipient.recipientDataControllerName ??
                      t("dataController.unknown"),
                  },
                ),
              ]),
            ]}
            bannerType={BannerList.WARNING}
          />
        </Box>
      )}
      {hasIndependentWarning && (
        <Box sx={{ mb: 4 }}>
          <Banner
            title={t("dataController.sharingToIndependentContext", {
              count: uniqueIndependentRecipientNames.length,
            })}
            body={
              uniqueIndependentRecipientNames.length > 0
                ? uniqueIndependentRecipientNames.map((name) => [name])
                : undefined
            }
            bannerType={BannerList.INFO}
          />
        </Box>
      )}
    </>
  );
}
